import React, { useState, useRef, useEffect } from 'react';
import { v4 as uuid } from 'uuid';

// Material ui
import { Grid, TextField } from '@material-ui/core';

// Components
import Typography from '@components/UI-KIT/Typography';

// Styles
import '../Inputs.scss';

const allowedKeysInNumberInput = [
    '0',
    'Control',
    'Shift',
    'Delete',
    'Home',
    'End',
    'Alt',
    'Backspace',
    'Tab',
    'ArrowRight',
    'ArrowLeft'
];

interface Props {
    value: string | number;
    onChange: (_value: string) => void;
    autoFocus?: boolean;
    disabled?: boolean;
    label?: string;
    className?: string;
    placeholder?: string;
    name?: string;
    id?: string;
    optional?: boolean;
    variant?: string;
    maxLength?: number;
    md?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    lg?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    sm?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    xs?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
    type?: 'text' | 'number';
    allowLeadingZeros?: boolean;
    suffix?:string;
    inputErrorCondition?: (_value) => boolean;
    inputErrorMenssage?: string;
}

const TextInput: React.FC<Props> = ({
    value,
    variant = 'primary',
    onChange,
    autoFocus = false,
    disabled = false,
    label = '',
    placeholder,
    name,
    id,
    optional = false,
    type = 'text',
    className,
    maxLength = 72,
    md,
    lg,
    sm,
    xs,
    allowLeadingZeros,
    inputErrorCondition = () => false,
    inputErrorMenssage = '',
    suffix = ''
}) => {
    const [ { errorMsg, inputError }, setError ] = useState({
        errorMsg: '',
        inputError: false
    });
    const inputRef = useRef<HTMLInputElement>();
    const idRef = useRef(uuid());
    const elementRef = useRef<HTMLDivElement>();

    const validateInput = (inputValue: string) => {
        if(inputErrorCondition(inputValue)) setError({
            inputError: true,
            errorMsg: inputErrorMenssage
        });
        else if (optional) return;
        else if (inputValue.length === 0) setError({
            inputError: true,
            errorMsg: 'Este campo es requerido'
        });
        else setError({
            inputError: false,
            errorMsg: ''
        });
    };

    // TODO optional: use prevValue instead of substr
    const validateTypeNumberInput = (value: string | number) => {
        if (type !== 'number' && typeof value === 'string') return value.substr(0, maxLength);
        else {
            if (allowLeadingZeros) {
                return value.toString().substr(0, maxLength);
            }
            return value = Number.parseInt(value.toString().substr(0, maxLength));
        }
    };

    useEffect(() => {
        if (type === 'number') {
            elementRef.current.addEventListener('wheel', e => e.preventDefault());
            elementRef.current.addEventListener('keydown', e => {
                if (!Number(e.key) && !allowedKeysInNumberInput.includes(e.key)) e.preventDefault();
            });
        }
    }, [ ]);

    // Handle focus event
    useEffect(() => {
        const handleFocus = (e: FocusEvent) => {
            const target = (e.target as HTMLInputElement);
            if (target && target.parentElement)
                target.parentElement.parentElement.id = target.parentElement.parentElement.id === 'ui-Input-focused' ? idRef.current : 'ui-Input-focused';
        };
        if (!inputRef.current) return;
        inputRef.current.addEventListener('focus', handleFocus);
        inputRef.current.addEventListener('blur', handleFocus);
    }, [ inputRef ]);

    return (
        <Grid
            className={`ui-TextInput${className ? ` ${className}` : ''}`}
            item
            md={md}
            lg={lg}
            sm={sm}
            xs={xs === 1 ? undefined : xs}
            style={{
                width: xs === 1 ? '6rem' : ''
            }}
        >
            <Typography
                variant="input-head"
                className="ui-Input-label"
                color={disabled ? 'content-light' : 'primary-dark'}
            >
                {`${label}${optional ? ' (opcional)' : ''}`}
            </Typography>
            <TextField
                value={value ? validateTypeNumberInput(value).toString() : ''}
                id={id}
                ref={elementRef}
                type={type === 'number' ? 'tel' : 'text'}
                className={`ui-Input-container ${variant}${errorMsg ? ' error-border' : ''}`}
                name={name}
                autoFocus={autoFocus}
                disabled={disabled}
                placeholder={placeholder}
                size="small"
                variant="outlined"
                onChange={({ target }) => {
                    onChange(target.value);
                    validateInput(target.value);
                }}
                inputProps={{
                    ref: inputRef
                    // maxLength: {maxLength}
                }}
            />
            {
                value && (
                    <Typography
                        className="container-suffix"
                    >
                        {suffix}
                    </Typography>
                )
            }
            { inputError &&  (
                <Typography
                    variant="note-regular"
                    color="error"
                    className="ui-Error-msg"
                >
                    {errorMsg}
                </Typography>
            )}
        </Grid>
    );
};

export default TextInput;
